import { NgModule, LOCALE_ID } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';
import { RouterModule, Routes } from '@angular/router';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { HttpClientModule, HTTP_INTERCEPTORS } from '@angular/common/http';
import { registerLocaleData } from '@angular/common';
import localeFr from '@angular/common/locales/fr';
import localeEn from '@angular/common/locales/en';

// import 'hammerjs';
import { TranslateModule, TranslateLoader } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { HttpClient } from '@angular/common/http';
import { ToastrModule } from 'ngx-toastr'; // For auth after login toast

import { CoreModule } from '@core/core.module';

import { coreConfig } from 'app/app-config';

import { AppComponent } from 'app/app.component';
import { LayoutModule } from 'app/layout/layout.module';
import { DashboardModule } from './main/dashboard/dashboard.module';
import { AuthInterceptor } from './interceptors/auth/auth.interceptor';
import { AuthenticationModule } from './main/pages/authentication/authentication.module';
import { ResponseInterceptor } from './interceptors/response/response.interceptor';
import { SharedModule } from './main/shared/shared.module';
import { ServiceWorkerModule } from '@angular/service-worker';
import { environment } from '../environments/environment';
import { StudentRegistrationComponent } from './main/learners/student-registration/student-registration.component';
import { FormsModule, ReactiveFormsModule } from '@angular/forms';
import { OnlineAccessModule } from './main/online-access/online-access.module';

// Register locale data
registerLocaleData(localeFr, 'fr');
registerLocaleData(localeEn, 'en');

// Factory function for TranslateHttpLoader
export function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

const appRoutes: Routes = [
  {
    path: 'app',
    children: [
      {
        path: 'learners',
        loadChildren: () =>
          import('./main/learners/learners.module').then(
            (m) => m.LearnersModule
          ),
      },
      { path: 'register-student', component: StudentRegistrationComponent },
      {
        path: 'courses',
        loadChildren: () =>
          import('./main/courses/courses.module').then((m) => m.CoursesModule),
      },
      {
        path: 'academic-level',
        loadChildren: () =>
          import('./main/academic-level/academic-level.module').then(
            (m) => m.AcademicLevelModule
          ),
      },
      {
        path: 'academic-year',
        loadChildren: () =>
          import('./main/academic-year/academic-year.module').then(
            (m) => m.AcademicYearModule
          ),
      },
      {
        path: 'attendance',
        loadChildren: () =>
          import('./main/attendance/attendance.module').then(
            (m) => m.AttendanceModule
          ),
      },
      {
        path: 'exams',
        loadChildren: () =>
          import('./main/exams/exams.module').then((m) => m.ExamsModule),
      },
      {
        path: 'results',
        loadChildren: () =>
          import('./main/results/results.module').then((m) => m.ResultsModule),
      },
      {
        path: 'assignments',
        loadChildren: () =>
          import('./main/assignments/assignments.module').then(
            (m) => m.AssignmentsModule
          ),
      },
      {
        path: 'evaluations',
        loadChildren: () =>
          import('./main/evaluation/evaluation.module').then(
            (m) => m.EvaluationModule
          ),
      },
      {
        path: 'lectures',
        loadChildren: () =>
          import('./main/lectures/lectures.module').then(
            (m) => m.LecturesModule
          ),
      },
      {
        path: 'staff',
        loadChildren: () =>
          import('./main/staff/staff.module').then((m) => m.StaffModule),
      },
      {
        path: 'timetable',
        loadChildren: () =>
          import('./main/timetable/timetable.module').then(
            (m) => m.TimetableModule
          ),
      },
      {
        path: 'payments',
        loadChildren: () =>
          import('./main/payments/payments.module').then(
            (m) => m.PaymentsModule
          ),
      },
      {
        path: 'complains',
        loadChildren: () =>
          import('./main/complains/complains.module').then(
            (m) => m.ComplainsModule
          ),
      },
      {
        path: 'admin',
        loadChildren: () =>
          import('./main/pages/pages.module').then((m) => m.PagesModule),
      },
      {
        path: 'classrooms',
        loadChildren: () =>
          import('./main/classroom/classroom.module').then(
            (m) => m.ClassroomModule
          ),
      },
      {
        path: 'semester',
        loadChildren: () =>
          import('./main/semester/semester.module').then(
            (m) => m.SemesterModule
          ),
      },
      {
        path: 'sequence',
        loadChildren: () =>
          import('./main/sequence/sequence.module').then(
            (m) => m.SequenceModule
          ),
      },
      {
        path: 'series',
        loadChildren: () =>
          import('./main/series/series.module').then((m) => m.SeriesModule),
      },
      {
        path: 'specialty',
        loadChildren: () =>
          import('./main/specialty/specialty.module').then(
            (m) => m.SpecialtyModule
          ),
      },
      {
        path: 'departments',
        loadChildren: () =>
          import('./main/departments/departments.module').then(
            (m) => m.DepartmentsModule
          ),
      },
      {
        path: 'roles',
        loadChildren: () =>
          import('./main/roles/roles.module').then((m) => m.RolesModule),
      },
      {
        path: 'fees',
        loadChildren: () =>
          import('./main/fees/fee-structure/fees.module').then((m) => m.FeesModule),
      },
      {
        path: 'course-assignment',
        loadChildren: () =>
          import('./main/course-assignment/course-assignment.module').then((m) => m.CourseAssignmentModule),
      },
      {
        path: 'promotion',
        loadChildren: () =>
          import('./main/promotion/promotion.module').then((m) => m.PromotionModule),
      },

      {
        path: 'classroom-progression',
        loadChildren: () =>
          import('./main/classroom-progression/classroom-progression.module').then((m) => m.ClassroomProgressionModule),
      },
      {
        path: 'custom-exam',
        loadChildren: () =>
          import('./main/custom-exam/custom-exam.module').then((m) => m.CustomExamModule),
      },
      // Staff Attendance Module
      {
        path: 'staff-attendance',
        loadChildren: () =>
          import('./main/staff-attendance/staff-attendance.module').then((m) => m.StaffAttendanceModule),
      },
      // Enhanced Fees Module (Mobile Money)
      {
        path: 'fees-enhanced',
        loadChildren: () =>
          import('./main/fees-enhanced/fees-enhanced.module').then((m) => m.FeesEnhancedModule),
      },
      // Assignments Enhanced (Submissions & Grading)
      {
        path: 'assignments-enhanced',
        loadChildren: () =>
          import('./main/assignments-enhanced/assignments-enhanced.module').then((m) => m.AssignmentsEnhancedModule),
      },
      // Lectures Enhanced (Materials & Notes)
      {
        path: 'lectures-enhanced',
        loadChildren: () =>
          import('./main/lectures-enhanced/lectures-enhanced.module').then((m) => m.LecturesEnhancedModule),
      },
      // Staff Portal (New Mobile-First Staff Dashboard)
      {
        path: 'staff-portal',
        loadChildren: () => import('./main/staff-portal/staff-portal.module').then(m => m.StaffPortalModule),
      },
      // Staff Portal with teacherId param (Admin viewing specific teacher)
      {
        path: 'staff-portal/teacher/:teacherId',
        loadChildren: () => import('./main/staff-portal/staff-portal.module').then(m => m.StaffPortalModule),
      },
    ],
  },

  // {
  //   path: '**',
  //   redirectTo: '/app/dashboard', // Redirect to dashboard home as fallback
  // },
];

@NgModule({
  declarations: [AppComponent, StudentRegistrationComponent],
  imports: [
    BrowserModule,
    BrowserAnimationsModule,
    HttpClientModule,
    FormsModule,
    ReactiveFormsModule,
    // LearnersModule,
    RouterModule.forRoot(appRoutes, {
      scrollPositionRestoration: 'enabled'
    }),
    TranslateModule.forRoot({
      loader: {
        provide: TranslateLoader,
        useFactory: HttpLoaderFactory,
        deps: [HttpClient]
      },
      defaultLanguage: 'en',
      useDefaultLang: true
    }),

    //NgBootstrap
    // NgbModule,
    ToastrModule.forRoot(),

    // Core modules
    CoreModule.forRoot(coreConfig),
    // CoreCommonModule,
    // CoreSidebarModule,
    // CoreThemeCustomizerModule,

    // App modules
    LayoutModule,
    DashboardModule,
    AuthenticationModule,
    OnlineAccessModule,
    SharedModule,
    ServiceWorkerModule.register('ngsw-worker.js', {
      enabled: environment.production,
      registrationStrategy: 'registerWhenStable:30000'
    }),
  ],
  providers: [
    {
      provide: HTTP_INTERCEPTORS,
      useClass: AuthInterceptor,
      multi: true,
    },
    {
      provide: HTTP_INTERCEPTORS,
      useClass: ResponseInterceptor,
      multi: true,
    },
    {
      provide: LOCALE_ID,
      useValue: 'en'
    },
  ],
  bootstrap: [AppComponent],
})
export class AppModule { }
